/**
 * Tutor Modal Component
 * Displays detailed tutor information in a modal
 */

import React from 'react';
import { 
  X, 
  GraduationCap, 
  MapPin, 
  Calendar, 
  BookOpen, 
  Trophy, 
  Star,
  CheckCircle
} from 'lucide-react';
import { formatDistance } from 'date-fns';
import MessageButton from '../../../features/messaging/components/MessageButton';

const TutorModal = ({ tutor, isOpen, onClose }) => {
  if (!isOpen || !tutor) return null;

  // Calculate average grade
  const getAverageGrade = () => {
    if (!tutor.courses || tutor.courses.length === 0) return 0;
    
    const totalMarks = tutor.courses.reduce((sum, course) => sum + (course.finalMark || 0), 0);
    return Math.round(totalMarks / tutor.courses.length);
  };

  // Get grade color
  const getGradeColor = (grade) => {
    if (grade >= 90) return 'text-green-600 bg-green-50';
    if (grade >= 80) return 'text-blue-600 bg-blue-50';
    if (grade >= 70) return 'text-yellow-600 bg-yellow-50';
    if (grade >= 60) return 'text-orange-600 bg-orange-50';
    return 'text-red-600 bg-red-50';
  };

  // Get grade text color only
  const getGradeTextColor = (grade) => {
    if (grade >= 90) return 'text-green-600';
    if (grade >= 80) return 'text-blue-600';
    if (grade >= 70) return 'text-yellow-600';
    if (grade >= 60) return 'text-orange-600';
    return 'text-red-600';
  };

  // Format date
  const formatDate = (date) => {
    if (!date) return 'Unknown';
    return formatDistance(new Date(date), new Date(), { addSuffix: true });
  };

  const averageGrade = getAverageGrade();

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
        {/* Background overlay */}
        <div 
          className="fixed inset-0 transition-opacity bg-gray-500 bg-opacity-75"
          onClick={onClose}
        />

        {/* Modal panel */}
        <div className="inline-block align-bottom bg-white rounded-t-xl text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-2xl sm:w-full sm:rounded-lg">
          {/* Header */}
          <div className="bg-white px-6 pt-6 pb-4">
            <div className="flex justify-between items-start">
              <div className="flex items-center space-x-4">
                <div className="w-16 h-16 rounded-full overflow-hidden">
                  {tutor.photos && tutor.photos.length > 0 ? (
                    <img 
                      src={tutor.photos[0]} 
                      alt={tutor.name} 
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center bg-green-100 text-green-600 font-medium text-xl">
                      {tutor.name?.charAt(0) || 'T'}
                    </div>
                  )}
                </div>
                
                <div>
                  <h2 className="text-xl font-semibold text-gray-900">
                    {tutor.name}
                  </h2>
                  <div className="flex items-center mt-1 text-gray-600">
                    <MapPin size={16} className="mr-1" />
                    <span>{tutor.university}</span>
                  </div>
                  <div className="flex items-center mt-1 text-sm text-gray-500">
                    <Calendar size={14} className="mr-1" />
                    <span>Joined {formatDate(tutor.submittedAt)}</span>
                  </div>
                </div>
              </div>
              
              <button 
                onClick={onClose}
                className="text-gray-400 hover:text-gray-500 focus:outline-none"
              >
                <X size={24} />
              </button>
            </div>
          </div>

          {/* Content */}
          <div className="px-6 pb-6 max-h-[70vh] overflow-y-auto">
            {/* Verification Status */}
            {tutor.requiresProof && (
              <div className="mb-6 p-3 bg-green-50 border border-green-200 rounded-lg flex items-center">
                <CheckCircle className="w-5 h-5 text-green-600 mr-2" />
                <div>
                  <p className="text-sm font-medium text-green-800">Verified Tutor</p>
                  <p className="text-xs text-green-700">Academic records have been verified</p>
                </div>
              </div>
            )}

            {/* Overall Performance */}
            {averageGrade > 0 && (
              <div className="mb-6">
                <h3 className="text-lg font-medium text-gray-900 mb-3 flex items-center">
                  <Trophy size={20} className="mr-2 text-yellow-500" />
                  Academic Performance
                </h3>
                
                <div className={`inline-flex items-center px-4 py-2 rounded-lg ${getGradeColor(averageGrade)}`}>
                  <Star size={18} className={`mr-2 ${getGradeTextColor(averageGrade)}`} />
                  <span className={`font-semibold ${getGradeTextColor(averageGrade)}`}>
                    {averageGrade}% Average Grade
                  </span>
                </div>
              </div>
            )}

            {/* Courses and Grades */}
            <div className="mb-6">
              <h3 className="text-lg font-medium text-gray-900 mb-3 flex items-center">
                <BookOpen size={20} className="mr-2 text-blue-500" />
                Courses & Grades ({tutor.courses?.length || 0})
              </h3>
              
              {tutor.courses && tutor.courses.length > 0 ? (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {tutor.courses
                    .sort((a, b) => (b.finalMark || 0) - (a.finalMark || 0))
                    .map((course, index) => (
                      <div 
                        key={index}
                        className="p-3 border border-gray-200 rounded-lg hover:bg-gray-50 transition"
                      >
                        <div className="flex justify-between items-center">
                          <div>
                            <p className="font-medium text-gray-800">
                              {course.courseCode}
                            </p>
                          </div>
                          <div className={`px-2 py-1 rounded text-sm font-medium ${getGradeColor(course.finalMark || 0)}`}>
                            {course.finalMark || 0}%
                          </div>
                        </div>
                      </div>
                    ))}
                </div>
              ) : (
                <p className="text-gray-500 text-center py-4">
                  No courses listed
                </p>
              )}
            </div>

            {/* Contact Information */}
            <div className="mb-6">
              
              <div className="bg-gray-50 p-4 rounded-lg">
                <div className="mb-2">

                </div>
                <div>
                  <span className="text-sm font-medium text-gray-600">University:</span>
                  <p className="text-gray-800">{tutor.university}</p>
                </div>
              </div>
            </div>

            {/* Approval Information */}
            {tutor.reviewedAt && (
              <div className="mb-6">
                <h3 className="text-lg font-medium text-gray-900 mb-3">
                  Verification Details
                </h3>
                
                <div className="bg-blue-50 p-4 rounded-lg">
                  <div className="flex items-center mb-2">
                    <CheckCircle className="w-4 h-4 text-blue-600 mr-2" />
                    <span className="text-sm font-medium text-blue-800">Approved Tutor</span>
                  </div>
                  <p className="text-sm text-blue-700">
                    Verified {formatDate(tutor.reviewedAt)}
                  </p>
                </div>
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="px-6 py-4 border-t border-gray-200 bg-gray-50 flex justify-end space-x-3">
            <button
              onClick={onClose}
              className="px-4 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-100 transition"
            >
              Close
            </button>
            
            <MessageButton
              userId={tutor.userId}
              variant="primary"
              className="px-4 py-2 rounded-md"
              showText={true}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default TutorModal;